import React from 'react';
import { Typography, Paper, Grid, Card, CardContent } from '@mui/material';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import TranslateIcon from '@mui/icons-material/Translate';
import PersonalVideoIcon from '@mui/icons-material/PersonalVideo';
import SecurityIcon from '@mui/icons-material/Security';
import UpdateIcon from '@mui/icons-material/Update';

const AITalkerOverview = () => {
  const features = [
    { 
      icon: <RecordVoiceOverIcon />, 
      title: "Natural Language Processing", 
      description: "Advanced NLP capabilities for human-like conversations and context understanding."
    },
    { 
      icon: <TranslateIcon />, 
      title: "Multi-language Support", 
      description: "Communicate in multiple languages to serve a global audience."
    },
    { 
      icon: <PersonalVideoIcon />, 
      title: "Customizable Personality", 
      description: "Tailor the AI Talker's personality to match your brand voice and communication style."
    },
    { 
      icon: <SecurityIcon />, 
      title: "Secure and Compliant", 
      description: "Built-in security features and compliance with data protection regulations."
    },
    { 
      icon: <UpdateIcon />, 
      title: "Continuous Learning", 
      description: "AI Talker improves over time through machine learning and regular updates."
    },
  ];

  return (
    <Paper elevation={0}>
      <Typography variant="h4" gutterBottom>AI Talker Overview</Typography>
      <Typography paragraph>
        AI Talker is our state-of-the-art artificial intelligence system designed to revolutionize 
        customer interactions and streamline communication processes. By leveraging advanced 
        natural language processing and machine learning technologies, AI Talker provides 
        human-like conversations across various channels.
      </Typography>
      <Typography variant="h6" gutterBottom>Key Features:</Typography>
      <Grid container spacing={3}>
        {features.map((feature, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card>
              <CardContent>
                {feature.icon}
                <Typography variant="h6" component="div">
                  {feature.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {feature.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Typography variant="h6" style={{ marginTop: '20px' }}>How AI Talker Works:</Typography>
      <Typography paragraph>
        1. <strong>Input Processing:</strong> AI Talker receives input from various channels (voice, text, etc.) and processes it using advanced NLP algorithms.
      </Typography>
      <Typography paragraph>
        2. <strong>Context Understanding:</strong> The system analyzes the input in context, considering previous interactions and user information.
      </Typography>
      <Typography paragraph>
        3. <strong>Response Generation:</strong> Based on its training and the current context, AI Talker generates an appropriate response.
      </Typography>
      <Typography paragraph>
        4. <strong>Output Delivery:</strong> The response is delivered back to the user through the appropriate channel, maintaining a consistent conversation flow.
      </Typography>
      <Typography paragraph>
        5. <strong>Learning and Improvement:</strong> Each interaction is used to refine and improve the AI Talker's performance over time.
      </Typography>
      <Typography variant="h6" style={{ marginTop: '20px' }}>Benefits of AI Talker:</Typography>
      <Typography paragraph>
        - 24/7 Availability: Provide round-the-clock support and engagement for your customers.
      </Typography>
      <Typography paragraph>
        - Scalability: Handle multiple conversations simultaneously without compromising on quality.
      </Typography>
      <Typography paragraph>
        - Consistency: Deliver consistent responses and adhere to your brand guidelines across all interactions.
      </Typography>
      <Typography paragraph>
        - Cost-Effective: Reduce operational costs while improving customer satisfaction and engagement.
      </Typography>
      <Typography paragraph>
        - Data Insights: Gain valuable insights from customer interactions to inform business decisions.
      </Typography>
      <Typography paragraph>
        AI Talker is more than just a chatbot; it's a comprehensive AI-powered communication solution 
        that can transform how you interact with your customers, streamline your operations, and 
        drive business growth.
      </Typography>
    </Paper>
  );
};

export default AITalkerOverview;
